import React, { useRef, useCallback, useState } from "react";
import styled from "styled-components";
import { MESSAGES } from "../../../messages";
import { greyText, compassLightGreen } from "../../../styles";
import { CompassParagraph } from "../styledComponents";
import { Icon, IconContainer } from "../../visuals/styledComponents";
import {
  RowContainer,
  ElementContainer,
} from "../../containers/styledComponents";
import { GradientProgressBar } from "./GradientProgressBar";

import fileUploadIcon from "../../../assets/svg/file_upload.svg";
import fileIcon from "../../../assets/svg/file_icon.svg";

// =============================================================================
// STYLED COMPONENTS
// =============================================================================

const DropZoneContainer = styled.div`
  width: 100%;
`;

const DropZone = styled.div<{ $isDragOver: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
  width: 100%;
  box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  background: ${({ $isDragOver }): string =>
    $isDragOver ? "rgba(0, 100, 80, 0.02)" : "white"};
  cursor: pointer;
  transition: all 0.2s ease;
`;

const DropZoneIcon = styled(IconContainer)<{ $isDragOver: boolean }>`
  transition: transform 0.2s ease-in-out;
  ${({ $isDragOver }): string => ($isDragOver ? "transform: scale(1.1);" : "")}
  &:hover {
    transform: scale(1.1);
  }
`;

const HiddenInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
  opacity: 0;
  pointer-events: none;
`;

const FileInfoCard = styled.div`
  width: 100%;
  border: 1px solid ${compassLightGreen};
  border-radius: 12px;
  padding: 12px 16px;
  margin-top: 16px;
`;

const ProgressBarWrapper = styled.div`
  margin-top: 16px;
`;

// =============================================================================
// TYPES
// =============================================================================

export interface FileDropZoneProps {
  /** Currently selected file */
  file: File | null;
  /** Whether file is being uploaded */
  isUploading: boolean;
  /** Callback when file is selected */
  onFileSelect: (file: File) => void;
  /** File type description (e.g., "PDF (Max. 25MB)") */
  fileTypesDescription?: string;
  /** File type label for the drop zone text (e.g., "PDF", "Excel or CSV") */
  fileTypeLabel?: string;
  /** Accept attribute for file input (e.g., ".pdf,application/pdf") */
  accept?: string;
  /** Whether to show the file info card when file is selected */
  showFileInfo?: boolean;
  /** Custom class name */
  className?: string;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Reusable file drop zone with drag & drop support and upload progress bar.
 *
 * @example
 * ```tsx
 * <FileDropZone
 *   file={selectedFile}
 *   isUploading={isUploading}
 *   onFileSelect={(file) => handleFileUpload(file)}
 *   fileTypesDescription="PDF (Max. 25MB)"
 *   fileTypeLabel="PDF"
 *   accept=".pdf,application/pdf"
 * />
 * ```
 */
export const FileDropZone: React.FC<FileDropZoneProps> = ({
  file,
  isUploading,
  onFileSelect,
  fileTypesDescription = MESSAGES.FILE_DROP.FILE_TYPES,
  fileTypeLabel = "file",
  accept,
  showFileInfo = true,
  className,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0];
      if (selectedFile) {
        onFileSelect(selectedFile);
      }
      // Reset input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [onFileSelect],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const droppedFile = e.dataTransfer.files?.[0];
      if (droppedFile) {
        onFileSelect(droppedFile);
      }
    },
    [onFileSelect],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  };

  return (
    <DropZoneContainer className={className}>
      {/* Drop Zone - hidden when uploading */}
      {!isUploading && !file && (
        <DropZone
          $isDragOver={isDragOver}
          onClick={handleClick}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <DropZoneIcon size={60} m={1} $isDragOver={isDragOver}>
            <Icon src={fileUploadIcon} alt="Upload file" />
          </DropZoneIcon>
          <ElementContainer>
            <CompassParagraph color={compassLightGreen} mr={5} bold>
              {MESSAGES.FILE_DROP.CLICK_TO_UPLOAD}
            </CompassParagraph>
            <CompassParagraph mr={5}>
              {MESSAGES.FILE_DROP.DRAG_AND_DROP}
            </CompassParagraph>
            <CompassParagraph mr={5}>{fileTypeLabel}</CompassParagraph>
            <CompassParagraph>{MESSAGES.FILE_DROP.HERE}</CompassParagraph>
          </ElementContainer>
          <CompassParagraph fontSize={12} color={greyText} mt={10}>
            {fileTypesDescription}
          </CompassParagraph>
          <HiddenInput
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={handleInputChange}
          />
        </DropZone>
      )}

      {/* File Info Card with Progress Bar - shown during/after upload */}
      {(isUploading || file) && showFileInfo && (
        <FileInfoCard>
          <RowContainer jc="flex-start" ai="center">
            <IconContainer size={50} m={1}>
              <Icon src={fileIcon} alt="file icon" />
            </IconContainer>
            <ElementContainer flexDirection="column" ml={15}>
              <CompassParagraph bold fontSize={16}>
                {file?.name || "Uploading..."}
              </CompassParagraph>
              {file && (
                <CompassParagraph color={greyText} fontSize={13}>
                  {formatFileSize(file.size)}
                </CompassParagraph>
              )}
            </ElementContainer>
          </RowContainer>
          {isUploading && (
            <ProgressBarWrapper>
              <GradientProgressBar isLoading={true} />
            </ProgressBarWrapper>
          )}
        </FileInfoCard>
      )}

      {/* Hidden input always available for programmatic triggering */}
      {(isUploading || file) && (
        <HiddenInput
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleInputChange}
        />
      )}
    </DropZoneContainer>
  );
};
